<script>

// This code was adapted from:

// https://github.com/MadimetjaShika/vuetify-google-autocomplete

// which was itself adapted from:

// https://github.com/olefirenko/vue-google-autocomplete


import { VTextField } from 'vuetify/lib';

export default {
  name: 'simple-google-autocomplete',

  extends: VTextField,

  props: {
    /**
     * Select Address Types and Address Component Types
     * https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
     */
    addressComponents: {
      type: Object,
      default: () => ({
        street_number: 'short_name',
        route: 'long_name',
        locality: 'long_name',
        administrative_area_level_1: 'short_name',
        administrative_area_level_2: 'short_name',
        country: 'long_name',
        postal_code: 'short_name',
      }),
    },

    selectionRequired: {
      type: Boolean,
      default: false,
    },
    
    selectionRequiredText: {
      type: String,
      default: 'Please select an address from the list.',
    },
    
    /**
     * Types supported in place autocomplete requests.
     *
     * @alias module:vuetify-google-autocomplete.props.types
     * @see {@link https://developers.google.com/places/supported_types#table3}
     * @see {@link https://developers.google.com/places/web-service/autocomplete}
     * @type {String}
     */
    types: {
      type: String,
      default: 'address',
    },
  },

  data: () => ({
    /**
     * The Autocomplete object.
     * @access private
     * @type {Autocomplete}
     * @link https://developers.google.com/maps/documentation/javascript/reference#Autocomplete
     */
    gAutocomplete: null,

    /**
     * Autocomplete input text
     * @access private
     * @type {String}
     */
    autocompleteText: '',

    /**
     * Indicates if the Geolocate has already been set.
     * @access private
     */
    geolocateSet: false,

    /**
     * Interval for loading Google Maps.
     * @access private
     */
    loadInterval: null,

    /**
     * Global Google Maps State Watcher.
     * @access private
     */
    vgaMapState: null,

    /**
     * The address of the last place the user selected.
     * @access private
     * @type {String}
     */
    lastSelectedPlace: '',
  }),
  
  computed: {
    rulesPlusInternalRules() {
      const enforceSelectionRequired = [];

      if (this.selectionRequired
        && (this.lastSelectedPlace.trim() === '' || this.lastSelectedPlace !== this.autocompleteText)) {
        enforceSelectionRequired.push(this.selectionRequiredText);
      } else {
        enforceSelectionRequired.push(true);
      }

      return [...this.rules, ...enforceSelectionRequired];
    },

  },

  methods: {
    /**
     * The error.sync event
     * @access private
     */
    onUpdateError() {
      this.$emit('update:error');
    },

    onChange() {
      this.$emit('change', this.autocompleteText);
    },

    /**
     * Clear the input
     * @access private
     */
    clear() {
      this.autocompleteText = '';
      this.$refs.textField.clearableCallback();
    },


    /**
     * Update the value of the input
     * @param {String} value The value to update to.
     * @access private
     */
    update(value) {
      this.autocompleteText = value;
    },

    /**
     * Bias the autocomplete object to the user's geographical location,
     * as supplied by the browser's 'navigator.geolocation' object.
     * @access private
     */
    geolocate() {
      if (this.enableGeolocation && !this.geolocateSet) {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((position) => {
            const geolocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            // initialise up knowing that google maps is actually loaded
            this.setupGmapApi(() => {
              const circle = new window.google.maps.Circle({
                center: geolocation,
                radius: position.coords.accuracy,
              });
              this.gAutocomplete.setBounds(circle.getBounds());
              this.geolocateSet = true;
            });
          });
        }
      }
    },

    setupGoogle() {
      const options = {};

      if (this.types) {
        options.types = [this.types];
      }

      if (this.country) {
        options.componentRestrictions = {
          country: this.country,
        };
      }

      if (this.fields) {
        if (typeof this.fields === 'string') {
          options.fields = [this.fields];
        } else {
          options.fields = this.fields;
        }
      }

      // initialise up knowing that google maps is actually loaded
      this.setupGmapApi(() => {
        this.gAutocomplete = new window.google.maps.places.Autocomplete(
          document.getElementById(this.id),
          options,
        );
        
        // this insanity disables google from setting the autocomplete on the input to "off"
        document.getElementById(this.id).addEventListener(
          'focus',
          e => e.target.setAttribute('autocomplete', this.$attrs.autocomplete || 'off')
        );

        // this is potentially inside a promise so the autocomplete MUST exist to add the listener
        this.gAutocomplete.addListener('place_changed', () => {
          const place = this.gAutocomplete.getPlace();

          // if (!place || !place.geometry) {
          if (Object.keys(place).length < 2) {
            // User entered the name of a Place that was not suggested and
            // pressed the Enter key, or the Place Details request failed.
            this.$emit('no-results-found', place);
            return;
          }

          const returnData = {};

          if (place.name !== undefined && this.placeName) {
            this.autocompleteText = place.name;
          } else if (place.formatted_address !== undefined) {
            this.autocompleteText = place.formatted_address;
          }

          if (place.address_components !== undefined) {
            // Get each component of the address from the place details
            for (let i = 0; i < place.address_components.length; i += 1) {
              const addressType = place.address_components[i].types[0];

              if (this.addressComponents[addressType]) {
                const val = place.address_components[i][this.addressComponents[addressType]];
                returnData[addressType] = val;
              }
            }
            if (place.geometry) {
              returnData.latitude = place.geometry.location.lat();
              returnData.longitude = place.geometry.location.lng();
            }

            // additional fields available in google places results
            if (place.name) {
              returnData.name = place.name;
            }
            if (place.photos) {
              returnData.photos = place.photos;
            }
            if (place.place_id) {
              returnData.place_id = place.place_id;
            }

            // return returnData object and PlaceResult object
            this.$emit('placechanged', returnData, place, this.id);

            // update autocompleteText then emit change event
            this.lastSelectedPlace = this.autocompleteText;
            this.onChange();
            if (this.validateOnBlur) {
              // manually validate the underlying v-text-field because
              // selecting an option causes the field to lose focus
              // before the place_changed event is emitted and we have
              // a chance to fill the field with the formatted address
              this.$refs.textField.validate();
            }
          }
        });
      });


      // Override the default placeholder
      // text set by Google with the
      // placeholder prop value or an empty value.
      document.getElementById(this.id)
        .setAttribute('placeholder', this.placeholder ? this.placeholder : '');
    },

    /**
     * Uses the gmap deferred loading strategy from the third-party library rather than the
     * default strategy of this library.
     *
     * @remarks
     *  fixes https://github.com/MadimetjaShika/vuetify-google-autocomplete/issues/60
     * @private
     */
    setupGmapApi(callback) {
      if (this.$vueGoogleMapsCompatibility) {
        if (Object.prototype.hasOwnProperty.call(this, '$gmapApiPromiseLazy')) {
          this.$gmapApiPromiseLazy()
            .then(() => callback());
        }
      } else if (Object.prototype.hasOwnProperty.call(window, 'google')) {
        callback();
      } else {
        // no logger with this library. But this is a likely configuration error condition
        // Note: unwilling to throw an Error
      }
    },
  },

  /**
   * @desc Updates the autocompleteText member if a v-model was provided.
   */
  created() {
    this.autocompleteText = this.value ? this.value : '';
    this.lastSelectedPlace = this.autocompleteText;
  },

  /**
   * @desc Loads the Google Autocomplete SDK.
   */
  mounted() {
    this.vgaMapState = window.vgaMapState
    this.setupGmapApi(this.setupGoogle)
  },

  /**
   * @desc Resets the autocomplete loaded state.
   */
  destroyed() {
    // trip this on the way out so we can differentiate return trips in mounted()
    window.vgaMapState.initMap = false;
  },

  watch: {
    // Emit the new autocomplete text whenever it changes.
    autocompleteText(newVal) {
      this.$emit('input', newVal || '');
    },

    // Keep autocompleteText up-to-date with v-model.
    value(newVal) {
      if (newVal !== this.autocompleteText) {
        this.autocompleteText = newVal;
        this.lastSelectedPlace = newVal;
      }
    },

    // Update the SDK country option whenever it changes from the parent.
    country(newVal) {
      if (newVal) {
        this.gAutocomplete.componentRestrictions.country = newVal;
      }
    },

    // Update the SDK fields option whenever it changes from the parent.
    fields(newVal) {
      if (newVal) {
        if (typeof newVal === 'string') {
          this.setFields([newVal]);
        } else {
          this.setFields(newVal);
        }
      }
    },

    // Watches for changes on the Geolocation option.
    enableGeolocation(newVal) {
      if (!newVal) {
        this.geolocateSet = false;
      }

      this.enableGeolocation = newVal;
    },

    /**
     * Watches the internal initialisation flag (callback from google maps loading) and
     * when changes works out the google maps load strategy. In the case that requires
     * vue2-google-maps compatibility the known lazy load strategy from that library is used.
     */
    'vgaMapState.initMap': function vgaMapStateInitMap(value) {
      if (value) {
        this.setupGmapApi(this.setupGoogle);
      }
    },

    'window.vueGoogleMapsInit': function vgaMapStateInitMap() {
      window.vgaMapState.initMap = true;
    },

    // Update the SDK types option whenever it changes from the parent.
    types(newVal) {
      if (newVal) {
        this.gAutocomplete.setTypes([this.types]);
      }
    },
  },
};
</script>